import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchSession, signOut as signOutRequest, type Session } from './api/auth'
import { releaseOnSignOut } from './api/push'
import { setSimulatedToday } from './api/calendar'

export const SESSION_KEY = ['session']

/**
 * The signed-in account, or null.
 *
 * `isPending` matters as much as the data: rendering a redirect-to-login while
 * the session is still being fetched bounces an authenticated user off the page
 * they asked for on every refresh.
 */
export function useSession() {
  const { data, isPending, isError } = useQuery({
    queryKey: SESSION_KEY,
    queryFn: fetchSession,

    // The server ends sessions the moment a capability is revoked (D-72), so a
    // long-cached "yes" would keep showing controls that no longer work.
    staleTime: 60_000,
    retry: false,
  })

  /*
   * The client's clock follows the server's (D-386).
   *
   * Set here because this is the one place the session arrives, and it is set
   * on **every** render rather than in an effect: `today()` is called during
   * render by the routes that redirect, so a value applied afterwards would be
   * one render too late and the first landing would use the real date.
   *
   * Null for an ordinary session, which restores the real clock — so a
   * simulated date cannot survive into the next person's session.
   */
  setSimulatedToday(data?.simulatedDate ?? null)

  return {
    session: data ?? null,
    isLoading: isPending,
    isError,
  }
}

/**
 * Whether the current account holds a capability.
 *
 * Always this, never a role comparison — matching how the server decides (A-3).
 * Roles here would be a second authorisation model that drifts from the first,
 * and the drift shows up as a button that does nothing.
 */
export function useCan(capability: string): boolean {
  const { session } = useSession()

  /*
   * Both levels guarded. The optional chain used to stop at `session`, so a
   * session that arrived without `capabilities` threw
   * "Cannot read properties of undefined" and took the whole screen down rather
   * than answering "no". A capability question has a safe answer when the
   * evidence is missing, and it is *no* — the server refuses anyway (A-3), so
   * hiding a control costs a click and crashing costs the page.
   */
  return session?.capabilities?.includes(capability) ?? false
}

export function useSignOut() {
  const queryClient = useQueryClient()

  return useMutation({
    /*
     * **The device is released before the session ends, not after** (D-530).
     *
     * The shared family iPad is the case: one endpoint can legitimately hold a
     * subscription for two accounts, so without this mum signs out, dad signs
     * in, and mum keeps getting notifications on a device she is no longer
     * using — and she cannot switch them off without signing back in on it.
     *
     * Before, because unsubscribing needs the cookie. It never throws and never
     * blocks: signing out matters more than tidying up after it.
     */
    mutationFn: async () => {
      await releaseOnSignOut()
      return signOutRequest()
    },
    onSuccess: () => {
      // Everything cached was fetched as someone. Clearing the lot avoids the
      // next person seeing the previous one's data for a frame.
      queryClient.clear()
      queryClient.setQueryData(SESSION_KEY, null)
    },
  })
}

export function useSetSession() {
  const queryClient = useQueryClient()
  return (session: Session) => queryClient.setQueryData(SESSION_KEY, session)
}
